<template>
  <div
    :id="id"
    class="rs-form-settings-select-options"
  >
    <Draggable
      id="drag_content_options"
      v-model="options"
      :item-key="(item: any) => item"
      drag-class="card-ghost"
      ghost-class="drop-preview"
    >
      <template #item="{ element: opt, index }">
        <div class="row mx-0 pb-2 align-items-center">
          <div class="col-12 p-0 row m-0 ">
            <!-- ICON TO DRAG -->
            <div
              class="flex-grow-0 cursor-grabbing draggable-card"
              :has-drag="true"
            >
              <i class="fas fa-grip-vertical rs-text-secondary-light" />
            </div>
            <div
              v-show="type !== 'checkbox-group'"
              class="p-0 flex-grow-0 ml-2"
              :title="$ts('formSettings.fieldConfig.toggleDefaultOption')"
            >
              <RsFormRadio
                :id="`${id}_${index}_radio_selected`"
                :model-value="options[index].selected"
                :value="true"
                @change="handleRadioChanged(index)"
              />
            </div>
            <!-- CHECKBOX -->
            <div
              v-show="type === 'checkbox-group'"
              class="p-0 flex-grow-0 ml-2"
              :title="$ts('formSettings.fieldConfig.toggleDefaultOption')"
            >
              <RsFormCheckbox
                :id="`${id}_${index}_checkbox_selected`"
                v-model="options[index].selected"
                :true-value="true"
                :false-value="false"
              />
            </div>
            <div class="col p-0 row m-0">
              <!-- LABEL -->
              <div class="col-6 pr-0 pl-3">
                <input
                  v-model.lazy.trim="opt.label"
                  type="text"
                  :placeholder="$ts('formSettings.fieldConfig.optionLabelPlaceholder')"
                  class="form-control"
                >
              </div>
              <!-- VALUE -->
              <div class="col-6 pr-0 pl-3">
                <input
                  v-model.lazy.trim="opt.value"
                  type="text"
                  :placeholder="$ts('formSettings.fieldConfig.optionValuePlaceholder')"
                  class="form-control"
                  @blur="handleValueBlur(index, opt.value)"
                >
              </div>
            </div>
            <!-- DELETE ICON -->
            <div
              class="p-0 flex-grow-0 ml-2"
            >
              <RsButton
                :style="{ visibility: type !== 'select' && index === 0 ? 'hidden' : 'initial' }"
                class="btn-toolbar-inline sm"
                @click="options.splice(index, 1)"
              >
                <i class="far fa-trash-alt rs-text-error" />
              </RsButton>
            </div>
          </div>
        </div>
      </template>
    </Draggable>
    <RsButton
      class="btn-toolbar-inline sm add-option"
      @click="options.push({ label: '', value: '', selected: false })"
    >
      <div class="d-flex align-items-center">
        <i class="far fa-plus d-block" />
        <span class="d-block">{{ $ts('formSettings.fieldConfig.addOption') }}</span>
      </div>
    </RsButton>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import Draggable from 'vuedraggable';
import { formatToOptionValue } from '@/shared';

export type TFormSettingSelectOptions = {
  label: string,
  value: string,
  selected?: boolean,
};

export default defineComponent({
  name: 'RsFormSettingsSelectOptions',

  components: {
    Draggable,
  },

  props: {
    id: {
      type: String,
      required: true,
    },

    value: {
      type: Array as PropType<TFormSettingSelectOptions[]>,
      default: () => [],
    },

    /**
     * Vue 3 v-model binding, kept alongside the legacy `value`/`input` pair so
     * both `v-model` and explicit `:value`+`@input` consumers work. `undefined`
     * means the parent did not bind v-model (see the `boundValue` computed).
     */
    modelValue: {
      type: Array as PropType<TFormSettingSelectOptions[]>,
      default: undefined,
    },

    type: {
      type: String,
      default: 'select',
    },
  },

  emits: ['input', 'update:modelValue'],

  data() {
    return {
      options: this.modelValue !== undefined ? this.modelValue : this.value,
      isAssigned: false as boolean,
    };
  },

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): TFormSettingSelectOptions[] {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
  },

  watch: {
    boundValue: {
      handler() {
        if (!this.isAssigned) {
          this.options = this.boundValue;
          this.isAssigned = true;

          if (this.type !== 'checkbox-group') {
            this.handleRadioChanged(this.boundValue.findIndex((v: any) => v.selected));
          }
        }
      },
    },

    options: {
      deep: true,
      handler() {
        this.$emit('input', this.options);
        this.$emit('update:modelValue', this.options);
      },
    },
  },

  mounted() {
    if (this.type !== 'checkbox-group') {
      this.handleRadioChanged(this.boundValue.findIndex((v: any) => v.selected));
    }
  },

  methods: {
    handleRadioChanged(selectedIdx: number) {
      if (selectedIdx < 0) return;

      this.options.forEach((o: any, idx: number) => {
        (this.options as any)[idx].selected = false;
      });

      (this.options as any)[selectedIdx].selected = true;
      this.$forceUpdate();
    },
    handleValueBlur(index: number, value: string) {
      (this.options[index] as any).value = formatToOptionValue(value);
    },
  },
});
</script>

<style lang="scss">
@import "@/styles/_variables";

.rs-form-settings-select-options {
  .rs-radio {
    min-height: auto;

    label {
      display: block;
      height: 16px;
    }
  }
  .add-option {
    color: $tertiary-muted !important;
    opacity: 0.7;
    font-size: 14px !important;
  }
}

</style>
